import { useEffect } from "react";
import { isFormControlFocused } from "utils/isFormControlFocused";
import { hasOpenModal } from "components/common/Modal/modalStack";
import type { UseUndoRedoShortcuts } from "./types";

export const useUndoRedoShortcuts: UseUndoRedoShortcuts = ({
  onUndo,
  onRedo,
  isDisabled,
}) => {
  useEffect(() => {
    if (isDisabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      // A held key repeats; one press must revert one change.
      if (event.repeat || hasOpenModal()) return;
      // A clicked checkbox keeps focus, and Ctrl+Z means nothing there.
      const isToggleFocused =
        event.target instanceof HTMLInputElement &&
        (event.target.type === "checkbox" || event.target.type === "radio");
      if (isFormControlFocused(event) && !isToggleFocused) return;

      // event.key is the typed letter, so a German keyboard's Z is "z" too; event.code would be "KeyY" there.
      const key = event.key.toLowerCase();
      if (key === "z") {
        event.preventDefault();
        if (event.shiftKey) onRedo();
        else onUndo();
      } else if (key === "y" && !event.shiftKey) {
        event.preventDefault();
        onRedo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onUndo, onRedo, isDisabled]);
};
